// src/components/MeasurementOverlay.tsx
/**
 * MeasurementOverlay Component for T-16: Measurement & Distance Tool
 * 
 * Displays measurement line and delta value when measurement tool is active.
 * Shows visual feedback during measurement operations.
 * 
 * Updated to support both horizontal and vertical orientations.
 */

import React from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { calculateMeasurement } from '../lib/measurement';
import MathRenderer from './MathRenderer';
import { toggleMeasurementSelection } from '../store/slices/uiSlice';
import { approximate, formatFractionForMath } from '../lib/fractions';

interface MeasurementOverlayProps {
  width: number;
  height: number;
  domain: { start: number; end: number };
  isHorizontal: boolean;
  valueToPixel: (value: number) => number;
  lineDenominator?: number;
  labelFormat?: 'fraction' | 'decimal';
  decimalPlaces?: number;
  hideLabels?: boolean;
}

export const MeasurementOverlay: React.FC<MeasurementOverlayProps> = ({
  width,
  height,
  domain,
  isHorizontal,
  valueToPixel,
  lineDenominator,
  labelFormat,
  decimalPlaces,
  hideLabels = false,
}) => {
  const dispatch = useAppDispatch();
  const measurement = useAppSelector(state => state.ui.measurement);
  const numberLine = useAppSelector(state => state.numberLines);
  
  // Calculate the line position based on orientation
  // Horizontal: line runs horizontally at vertical center (height / 2)
  // Vertical: line runs vertically at horizontal center (width / 2)
  const linePos = isHorizontal ? height / 2 : width / 2;
  
  const handleMeasurementClick = (event: React.MouseEvent, measurementId: string) => {
    event.stopPropagation();
    dispatch(toggleMeasurementSelection({ 
      id: measurementId, 
      ctrlKey: event.ctrlKey || event.metaKey 
    }));
  };

  // Helper to get coordinates based on orientation
  const getCoords = (valuePx: number) => {
    if (isHorizontal) {
      return { x: valuePx, y: linePos };
    } else {
      return { x: linePos, y: valuePx };
    }
  };

  return (
    <g className="measurement-overlay">
      {/* Active measurement (being drawn) */}
      {measurement.isActive && measurement.startPoint && measurement.endPoint && (
        <g className="active-measurement">
          {(() => {
            const startPx = valueToPixel(measurement.startPoint.value);
            const endPx = valueToPixel(measurement.endPoint.value);
            const startCoords = getCoords(startPx);
            const endCoords = getCoords(endPx);
            
            return (
              <>
                <line
                  x1={startCoords.x}
                  y1={startCoords.y}
                  x2={endCoords.x}
                  y2={endCoords.y}
                  stroke="#e11d48"
                  strokeWidth="2"
                  strokeDasharray="4,2"
                />
                <circle 
                  cx={startCoords.x} 
                  cy={startCoords.y} 
                  r="4" 
                  fill="#e11d48" 
                />
                <circle 
                  cx={endCoords.x} 
                  cy={endCoords.y} 
                  r="4" 
                  fill="#e11d48" 
                />
              </>
            );
          })()}
        </g>
      )}

      {/* Completed measurements */}
      {measurement.measurements.map((meas) => {
        const startPx = valueToPixel(meas.startValue);
        const endPx = valueToPixel(meas.endValue);
        const startCoords = getCoords(startPx);
        const endCoords = getCoords(endPx);
        const result = calculateMeasurement(
          meas.startValue, 
          meas.endValue, 
          16, // maxDenominator
          meas.isSnapped,
          meas.snappedStartValue,
          meas.snappedEndValue
        );
        const isSelected = meas.selected;
        
        // Calculate selection outline dimensions based on orientation
        const selectionRect = isHorizontal
          ? {
              x: Math.min(startPx, endPx) - 8,
              y: linePos - 8,
              width: Math.abs(endPx - startPx) + 16,
              height: 16,
            }
          : {
              x: linePos - 8,
              y: Math.min(startPx, endPx) - 8,
              width: 16,
              height: Math.abs(endPx - startPx) + 16,
            };
        
        // Calculate label position based on orientation
        const labelPosition = isHorizontal
          ? {
              x: (startPx + endPx) / 2 - 45,
              y: linePos - 38,
            }
          : {
              x: linePos + 10,
              y: (startPx + endPx) / 2 - 18,
            };

        const useFractionDisplay =
          labelFormat === 'fraction' ||
          (labelFormat === undefined && numberLine?.numberType === 'fraction');
        const mixedFractions = numberLine?.fractionDisplay?.format !== 'improper';
        const fractionLatex = (() => {
          if (lineDenominator) {
            const aligned = Math.abs(result.delta * lineDenominator - Math.round(result.delta * lineDenominator)) < 0.001;
            if (aligned) {
              const numerator = Math.round(result.delta * lineDenominator);
              return `\\Delta = \\frac{${numerator}}{${lineDenominator}}`;
            }
          }
          return `\\Delta = ${formatFractionForMath(
            result.deltaFraction ?? approximate(result.delta, 100),
            { mixed: mixedFractions }
          )}`;
        })();
        const dp = Math.max(0, Math.min(decimalPlaces ?? 2, 6));
        const decimalDisplay = result.delta.toLocaleString(undefined, {
          maximumFractionDigits: dp,
          minimumFractionDigits: 0,
        });

        return (
          <g
            key={meas.id}
            className={`completed-measurement ${isSelected ? 'selected' : ''}`}
            // Prevent measurement tool from treating clicks on existing
            // measurements as the start of a new measurement.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => handleMeasurementClick(e, meas.id)}
            style={{ cursor: 'pointer' }}
          >
            {/* Selection outline */}
            {isSelected && (
              <rect
                x={selectionRect.x}
                y={selectionRect.y}
                width={selectionRect.width}
                height={selectionRect.height}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeDasharray="4,2"
              />
            )}

            {/* Measurement line */}
            <line
              x1={startCoords.x}
              y1={startCoords.y}
              x2={endCoords.x}
              y2={endCoords.y}
              stroke={isSelected ? "#3b82f6" : "#e11d48"}
              strokeWidth={isSelected ? "3" : "2"}
              strokeDasharray="4,2"
            />

            {/* End markers */}
            <circle
              cx={startCoords.x}
              cy={startCoords.y}
              r="4"
              fill={isSelected ? "#3b82f6" : "#e11d48"}
            />
            <circle
              cx={endCoords.x}
              cy={endCoords.y}
              r="4"
              fill={isSelected ? "#3b82f6" : "#e11d48"}
            />

            {/* Delta label */}
            {!hideLabels && (
              <foreignObject
                x={labelPosition.x}
                y={labelPosition.y}
                width="90"
                height="32"
                className="overflow-visible"
              >
                <div className={`flex items-center justify-center border rounded shadow-sm px-2 py-1 ${
                  isSelected
                    ? 'bg-blue-100 border-blue-300'
                    : 'bg-white border-gray-300'
                }`} style={{ fontSize: '14px', fontWeight: 700, minWidth: '90px', minHeight: '28px', lineHeight: 1.1 }}>
                  {useFractionDisplay ? (
                    <MathRenderer
                      expression={fractionLatex}
                      className="text-base"
                      style={{ color: isSelected ? '#2563eb' : '#dc2626' }}
                    />
                  ) : (
                    <span className={`font-mono ${
                      isSelected ? 'text-blue-600' : 'text-red-600'
                    }`}>
                      Δ = {decimalDisplay}
                    </span>
                  )}
                </div>
              </foreignObject>
            )}
          </g>
        );
      })}
    </g>
  );
};
